import React, { useEffect, useMemo, useState } from 'react';
import { useField } from 'react-final-form';
import { useDebounce } from 'usehooks-ts';

import { RoleOption } from './types';
import { useEleveoUserRolesQuery } from '../../../api/eleveoRolesQuery';
import { useIntegrationRolesQuery } from '../../../api/integrationRolesQuery';
import { useAuth } from '../../../app/AuthorizationProvider';
import { useIntegrations } from '../../../app/IntegrationsProvider';
import { IntegrationRequestBodyType } from '../../../constants/integrationConstants';
import { MILLIS_500 } from '../../../constants/timeConstants';
import { INTEGRATION_FORM_FIELDS, USER_SYNCHRONIZATION_FIELDS } from '../../../scene/integrations/types';
import { ETL_PERMISSIONS } from '../../../utils/keycloakUtils';
import { createRoleOption } from '../../../utils/roleUtils';
import { integrationRolesProviderStateMock } from '../../../utils/test/IntegrationRolesProviderMock';
import { ETL_INTEGRATION_TYPE, Values } from '../../configuration/types';

const IntegrationRolesContext = React.createContext(integrationRolesProviderStateMock);

const getCorrectPermission = (integration: Values<typeof ETL_INTEGRATION_TYPE>): Values<typeof ETL_PERMISSIONS> => {
  switch (integration) {
    case ETL_INTEGRATION_TYPE.wxcc:
      return ETL_PERMISSIONS.getRolesWebex;
    case ETL_INTEGRATION_TYPE.zoomcc:
      return ETL_PERMISSIONS.getRolesZoomCC;
    case ETL_INTEGRATION_TYPE.msteams:
      return ETL_PERMISSIONS.getRolesMSTeams;
    case ETL_INTEGRATION_TYPE.wxcalling:
      return ETL_PERMISSIONS.getRolesWebexCalling;
    default:
      throw new Error('Received invalid integration!');
  }
};

export const IntegrationRolesProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const { integrationType } = useIntegrations();

  const { userHasPermissions } = useAuth();
  const [internalState, setInternalState] = useState<IntegrationRequestBodyType>();
  const debouncedState = useDebounce(internalState, MILLIS_500);
  const [isDebounceInProgress, setIsDebounceInProgress] = useState(true);

  const {
    input: { value: clientId },
    meta: { valid: isClientIdValid },
  } = useField(INTEGRATION_FORM_FIELDS.id(integrationType), { subscription: { value: true, valid: true } });

  const {
    input: { value: organizationId },
    meta: { valid: isOrganizationIdValid },
  } = useField(INTEGRATION_FORM_FIELDS.organizationId, { subscription: { value: true, valid: true } });

  const {
    input: { value: clientSecret },
    meta: { valid: isClientSecretValid },
  } = useField(INTEGRATION_FORM_FIELDS.secret(integrationType), { subscription: { value: true, valid: true } });

  const {
    input: { value: apiUrl },
    meta: { valid: isApiUrlValid },
  } = useField(INTEGRATION_FORM_FIELDS.apiUrl(integrationType), { subscription: { value: true, valid: true } });

  const {
    input: { value: roleMapping },
  } = useField<Record<string, string[]>>(USER_SYNCHRONIZATION_FIELDS.roleMapping, {
    subscription: {
      value: true,
    },
  });

  useEffect(() => {
    setIsDebounceInProgress(false);
  }, [debouncedState]);

  useEffect(() => {
    const isDefined = !!(clientId && clientSecret && organizationId && apiUrl);
    const isValid = isClientIdValid && isClientSecretValid && isOrganizationIdValid && isApiUrlValid;

    if (!isDefined || !isValid) {
      setInternalState(undefined);
      return;
    }

    setInternalState({ clientId, clientSecret, organizationId, apiUrl });
    setIsDebounceInProgress(true);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    clientId,
    clientSecret,
    organizationId,
    apiUrl,
    isClientIdValid,
    isClientSecretValid,
    isOrganizationIdValid,
    isApiUrlValid,
  ]);

  const isPermittedForEleveoRoles = userHasPermissions([ETL_PERMISSIONS.getRolesKeycloak]);
  const {
    data: eleveoRoles,
    isError: isEleveoRolesError,
    isLoading: isEleveoRolesLoading,
  } = useEleveoUserRolesQuery({ enabled: isPermittedForEleveoRoles });

  const permissionToCheck = useMemo(() => getCorrectPermission(integrationType), [integrationType]);
  const isPermittedForIntegrationRoles = userHasPermissions([permissionToCheck]);
  const {
    data: integrationRoles,
    isError: isIntegrationRolesError,
    isLoading: isIntegrationRolesLoading,
  } = useIntegrationRolesQuery(integrationType, debouncedState, {
    enabled: isPermittedForIntegrationRoles,
    retry: 1, // prevent to make a lot of requests to Zoom CC / MS Teams endpoints via our BE.
  });

  /**
   * Change on integration roles which resulted in inconsistent role mapping. Some selected roles are not present
   * in integration roles, therefore these options are located in separate section and user is informed about it.
   *
   * If not resolved manually, these options will stay in configuration.
   */
  const conflictedOptions: RoleOption[] = useMemo(() => {
    if (!integrationRoles) {
      return [];
    }

    const allOptions = new Set<string>();

    Object.keys(roleMapping).forEach((eleveoRoleId) => {
      if (Array.isArray(roleMapping[eleveoRoleId])) {
        roleMapping[eleveoRoleId].forEach((integrationRoleId) => {
          allOptions.add(integrationRoleId);
        });
      }
    });

    integrationRoles.forEach((roleOption) => {
      allOptions.delete(roleOption.id);
    });

    return Array.from(allOptions).map(createRoleOption);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [integrationRoles]);

  const conflictedRoles: Record<string, string[]> = useMemo(() => {
    /**
     * `isEleveoRolesLoading || isDebounceInProgress` is to prevent showing warning notification prior to debounced
     * query (`useIntegrationRolesQuery`) has time to fetch data.
     */
    if (!eleveoRoles || isEleveoRolesLoading || isDebounceInProgress) {
      return {};
    }

    const newConflictedRoles: Record<string, string[]> = {};

    Object.keys(roleMapping).forEach((eleveoRoleId) => {
      if (!eleveoRoles.find((roleOption) => roleOption.id === eleveoRoleId)) {
        newConflictedRoles[eleveoRoleId] = roleMapping[eleveoRoleId];
      }
    });

    return newConflictedRoles;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roleMapping, eleveoRoles, isEleveoRolesLoading, isDebounceInProgress]);

  const isDefined = !!(clientId && clientSecret && organizationId && apiUrl);
  const isValid = isClientIdValid && isClientSecretValid && isOrganizationIdValid && isApiUrlValid;
  const value = {
    /**
     * Eleveo roles state
     */
    eleveoRoles: eleveoRoles ?? [],
    isEleveoRolesError,
    isEleveoRolesLoading: isDebounceInProgress || isEleveoRolesLoading,
    isPermittedForEleveoRoles,

    /**
     * Integration roles state
     */
    integrationRoles: integrationRoles ?? [],
    isIntegrationRolesError,
    isIntegrationRolesLoading: isDebounceInProgress || isIntegrationRolesLoading,
    isIntegrationDataFilled: !!isValid && isDefined,
    isPermittedForIntegrationRoles,

    /**
     * Mapping state
     */
    conflictedRoles,
    conflictedOptions,
    roleMapping,
  };

  return <IntegrationRolesContext.Provider value={value}>{children}</IntegrationRolesContext.Provider>;
};

export const IntegrationRolesConsumer = IntegrationRolesContext.Consumer;

export const useIntegrationRoles = () => React.useContext(IntegrationRolesContext);
